import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

// Get token from sessionStorage for authentication
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

// TypeScript interfaces for API responses

// Restaurant Finance List Item
export interface RestaurantFinanceItem {
  id: string;
  restaurantGeneratedID: string;
  name: string;
  email?: string;
  phone?: string;
  totalEarnings: number;
  pendingPayouts: number;
  growth: string; // API returns "0.0%" format
  commission: number;
  commissionRate?: number;
  status: "active" | "inactive" | "suspended";
  totalOrders?: number;
  joinedDate?: string;
  hasBankDetails?: boolean;
  action: string;
}

// Restaurant Finance Details
export interface RestaurantFinanceDetails {
  restaurant: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    status: "active" | "inactive" | "suspended";
    joinedDate: string;
    hasBankDetails: boolean;
  };
  financialSummary: {
    totalRevenue: number;
    deliveredRevenue: number;
    restaurantEarnings: number;
    totalCommission: number;
    commissionRate: number;
    pendingPayouts: number;
    pendingPayoutCount: number;
  };
  orderStatistics: {
    allTime: {
      totalOrders: number;
      deliveredOrders: number;
      successRate: string;
    };
    period: {
      days: number;
      ordersByStatus: {
        pending: number;
        delivered: number;
        cancelled: number;
        "mark ready": number;
        "pick up": number;
      };
      revenueByStatus: {
        pending: number;
        delivered: number;
        cancelled: number;
        "mark ready": number;
        "pick up": number;
      };
    };
  };
  payoutHistory: PayoutHistoryItem[];
  monthlyTrend: any[];
}

// Payout History Item (matching actual API response)
export interface PayoutHistoryItem {
  id: string;
  reference: string;
  restaurant: string;
  restaurantId: string;
  amount: number;
  grossAmount: number;
  commission: number;
  date: string;
  account: string;
  status: string;
  action: string;
  bankDetails: {
    bankName: string;
    accountName: string;
    accountNumber: number;
    bankCode: number;
    inUse: boolean;
    _id: string;
  };
  description: string;
}

// Order Details for Receipt
export interface OrderItem {
  id: string;
  name: string;
  image: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  mealDetails: {
    id: string;
    name: string;
    description: string;
    image: string;
  };
}

export interface PayoutReceipt {
  orderInfo: {
    id: string;
    orderNumber: string;
    status: string;
    orderDate: string;
    estimatedTime: string;
    totalAmount: number;
    riderId: string;
  };
  customerDetails: {
    id: string;
    name: string;
  };
  restaurantDetails: Array<{
    id: string;
    name: string;
    manager: string;
  }>;
  orderItems: OrderItem[];
}

// API Response interfaces
interface RestaurantFinanceListResponse {
  success: boolean;
  data: {
    restaurants: RestaurantFinanceItem[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
    };
  };
}

interface RestaurantFinanceDetailsResponse {
  success: boolean;
  data: RestaurantFinanceDetails;
}

interface PayoutReceiptResponse {
  success: boolean;
  data: PayoutReceipt;
}

// Payout History API Response
interface PayoutHistoryResponse {
  success: boolean;
  data: {
    payouts: PayoutHistoryItem[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
    };
  };
}

// Redux state interface
interface RestaurantFinanceState {
  restaurants: RestaurantFinanceItem[];
  currentRestaurantDetails: RestaurantFinanceDetails | null;
  payoutHistory: PayoutHistoryItem[];
  currentPayoutReceipt: PayoutReceipt | null;
  status: "idle" | "loading" | "success" | "failed";
  detailsStatus: "idle" | "loading" | "success" | "failed";
  payoutHistoryStatus: "idle" | "loading" | "success" | "failed";
  receiptStatus: "idle" | "loading" | "success" | "failed";
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  // Payout history pagination
  payoutTotalCount: number;
  payoutCurrentPage: number;
  payoutTotalPages: number;
}

const initialState: RestaurantFinanceState = {
  restaurants: [],
  currentRestaurantDetails: null,
  payoutHistory: [],
  currentPayoutReceipt: null,
  status: "idle",
  detailsStatus: "idle",
  payoutHistoryStatus: "idle",
  receiptStatus: "idle",
  error: null,
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  // Payout history pagination
  payoutTotalCount: 0,
  payoutCurrentPage: 1,
  payoutTotalPages: 1,
};

// Async thunks for API calls

// 1. Get all restaurants finance
export const getAllRestaurantsFinance = createAsyncThunk<
  RestaurantFinanceListResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "restaurantFinance/getAllRestaurantsFinance",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<RestaurantFinanceListResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/finance/restaurants?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Validate response structure
      if (
        !response.data ||
        !response.data.data ||
        !response.data.data.restaurants
      ) {
        console.error("Invalid API response structure:", response.data);
        return {
          success: false,
          data: {
            restaurants: [],
            pagination: {
              totalCount: 0,
              currentPage: 1,
              totalPages: 1,
              limit: 10,
            },
          },
        };
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurants finance"
      );
    }
  }
);

// 2. Get restaurant finance details
export const getRestaurantFinanceDetails = createAsyncThunk<
  RestaurantFinanceDetailsResponse,
  string,
  { rejectValue: string }
>(
  "restaurantFinance/getRestaurantFinanceDetails",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<RestaurantFinanceDetailsResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/finance/restaurants/${restaurantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch restaurant finance details"
      );
    }
  }
);

// 3. Get all payout history
export const getAllPayoutHistory = createAsyncThunk<
  PayoutHistoryResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "restaurantFinance/getAllPayoutHistory",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<PayoutHistoryResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/finance/payouts?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Validate response structure
      if (
        !response.data ||
        !response.data.data ||
        !response.data.data.payouts
      ) {
        console.error("Invalid payout history API response structure:", response.data);
        return {
          success: false,
          data: {
            payouts: [],
            pagination: {
              totalCount: 0,
              currentPage: 1,
              totalPages: 1,
              limit: 10,
            },
          },
        };
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payout history"
      );
    }
  }
);

// 4. Get payout receipt
export const getPayoutReceipt = createAsyncThunk<
  PayoutReceiptResponse,
  string,
  { rejectValue: string }
>(
  "restaurantFinance/getPayoutReceipt",
  async (payoutId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<PayoutReceiptResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/finance/payouts/${payoutId}/receipt`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payout receipt"
      );
    }
  }
);

// Redux slice
const restaurantFinanceSlice = createSlice({
  name: "restaurantFinance",
  initialState,
  reducers: {
    clearCurrentRestaurantDetails: (state) => {
      state.currentRestaurantDetails = null;
      state.detailsStatus = "idle";
    },
    clearCurrentPayoutReceipt: (state) => {
      state.currentPayoutReceipt = null;
      state.receiptStatus = "idle";
    },
    clearErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all restaurants finance
      .addCase(getAllRestaurantsFinance.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllRestaurantsFinance.fulfilled, (state, action) => {
        state.status = "success";
        state.restaurants = action.payload.data.restaurants;
        state.totalCount = action.payload.data.pagination.totalCount;
        state.currentPage = action.payload.data.pagination.currentPage;
        state.totalPages = action.payload.data.pagination.totalPages;
        state.error = null;
      })
      .addCase(getAllRestaurantsFinance.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch restaurants finance";
      })

      // Get restaurant finance details
      .addCase(getRestaurantFinanceDetails.pending, (state) => {
        state.detailsStatus = "loading";
        state.error = null;
      })
      .addCase(getRestaurantFinanceDetails.fulfilled, (state, action) => {
        state.detailsStatus = "success";
        state.currentRestaurantDetails = action.payload.data;
        state.error = null;
      })
      .addCase(getRestaurantFinanceDetails.rejected, (state, action) => {
        state.detailsStatus = "failed";
        state.error =
          action.payload || "Failed to fetch restaurant finance details";
      })

      // Get payout history
      .addCase(getAllPayoutHistory.pending, (state) => {
        state.payoutHistoryStatus = "loading";
        state.error = null;
      })
      .addCase(getAllPayoutHistory.fulfilled, (state, action) => {
        console.log('✅ Payout History API Success:', action.payload);
        console.log('📊 Payouts received:', action.payload.data.payouts.length);
        state.payoutHistoryStatus = "success";
        state.payoutHistory = action.payload.data.payouts;
        state.payoutTotalCount = action.payload.data.pagination.totalCount;
        state.payoutCurrentPage = action.payload.data.pagination.currentPage;
        state.payoutTotalPages = action.payload.data.pagination.totalPages;
        state.error = null;
      })
      .addCase(getAllPayoutHistory.rejected, (state, action) => {
        state.payoutHistoryStatus = "failed";
        state.error = action.payload || "Failed to fetch payout history";
      })

      // Get payout receipt
      .addCase(getPayoutReceipt.pending, (state) => {
        state.receiptStatus = "loading";
        state.error = null;
      })
      .addCase(getPayoutReceipt.fulfilled, (state, action) => {
        state.receiptStatus = "success";
        state.currentPayoutReceipt = action.payload.data;
        state.error = null;
      })
      .addCase(getPayoutReceipt.rejected, (state, action) => {
        state.receiptStatus = "failed";
        state.error = action.payload || "Failed to fetch payout receipt";
      });
  },
});

export const {
  clearCurrentRestaurantDetails,
  clearCurrentPayoutReceipt,
  clearErrors,
} = restaurantFinanceSlice.actions;

export default restaurantFinanceSlice.reducer;
